import React from 'react'
import { cn } from '@/lib/utils'
import { useSelector, useDispatch } from 'react-redux'
import { RootState } from '@/store'
import { updatePreviewResponse } from '@/store/slices/preview'
import {
    ZeroEmoji,
    OneEmoji,
    TwoEmoji,
    ThreeEmoji,
    FourEmoji,
    FiveEmoji,
    SixEmoji,
    SevenEmoji,
    EightEmoji,
    NineEmoji,
    TenEmoji,
} from '@/assets/builder/Emoji/NpsEmoji'

interface NpsScaleProps {
    onAutoProceed?: () => void
    isTransitioning?: boolean
}

const NpsScale: React.FC<NpsScaleProps> = ({
    onAutoProceed,
    isTransitioning = false,
}) => {
    const dispatch = useDispatch()
    const { selectedQuestionId } = useSelector((state: any) => state.preview)
    const question = useSelector((state: RootState) =>
        state.preview.questions.find(
            (q: any) => q.questionId === selectedQuestionId
        )
    )
    const savedResponse = useSelector(
        (state: RootState) => state.preview.previewResponses[selectedQuestionId]
    )
    if (!question) return null
    const displayType = question.config.displayType
    const minRating = 0
    const maxRating = 10
    const minLabel = question?.config?.minLabel || 'Unlikely'
    const maxLabel = question?.config?.maxLabel || 'Likely'
    const divisions = maxRating - minRating + 1
    const handleRatingClick = (rating: number) => {
        // Prevent selection during transition
        if (isTransitioning) return

        dispatch(
            updatePreviewResponse({
                questionId: selectedQuestionId,
                response: { value: rating },
            })
        )

        // Auto-proceed after brief delay for visual feedback
        if (onAutoProceed) {
            setTimeout(() => {
                onAutoProceed()
            }, 300)
        }
    }
    if (displayType === 'emoji') {
        const npsEmojis = [
            { Emoji: ZeroEmoji, label: '0' },
            { Emoji: OneEmoji, label: '1' },
            { Emoji: TwoEmoji, label: '2' },
            { Emoji: ThreeEmoji, label: '3' },
            { Emoji: FourEmoji, label: '4' },
            { Emoji: FiveEmoji, label: '5' },
            { Emoji: SixEmoji, label: '6' },
            { Emoji: SevenEmoji, label: '7' },
            { Emoji: EightEmoji, label: '8' },
            { Emoji: NineEmoji, label: '9' },
            { Emoji: TenEmoji, label: '10' },
        ]
        return (
            <div className="flex flex-wrap justify-center gap-2 w-full mt-4">
                {npsEmojis.map((item, index) => {
                    const value = index
                    const isActive =
                        savedResponse && value === Number(savedResponse?.value)
                    return (
                        <button
                            key={index}
                            onClick={() => handleRatingClick(value)}
                            className={cn(
                                'flex flex-col items-center justify-center px-2 py-2 rounded-lg transition',
                                isActive ? 'bg-gray-200' : 'bg-transparent',
                                'focus:outline-none border-none'
                            )}
                            style={{ minWidth: 35 }}
                        >
                            <span className="mb-1">
                                <item.Emoji />
                            </span>
                            <span className="text-[14px] whitespace-nowrap font-medium text-[#222]">
                                {item.label}
                            </span>
                        </button>
                    )
                })}
                <div className="w-full max-w-[470px] flex gap-2 justify-start mt-3 px-1">
                    <span
                        className="text-gray-500 font-[450] text-[13px] text-left"
                        style={{ minWidth: 35 }}
                    >
                        0 - {minLabel}
                    </span>
                    <span
                        className="text-gray-500 font-[450] text-[13px] text-right"
                        style={{ minWidth: 35 }}
                    >
                        10 - {maxLabel}
                    </span>
                </div>
            </div>
        )
    }
    // Number scale
    return (
        <>
            <div className="w-full flex max-w-[470px] overflow-hidden mt-4">
                {Array.from({ length: divisions }).map((_, index) => {
                    const value = index + minRating
                    let bgColor = ''
                    let textColor = 'text-black'
                    if (value >= 0 && value <= 6) {
                        bgColor = 'bg-[#FFB3B3]'
                    } else if (value >= 7 && value <= 8) {
                        bgColor = 'bg-[#FFE7A0]'
                    } else if (value >= 9 && value <= 10) {
                        bgColor = 'bg-[#B3F5D2]'
                    }
                    return (
                        <div
                            key={index}
                            onClick={() => handleRatingClick(value)}
                            className={cn(
                                'h-[50px] flex items-center justify-center cursor-pointer flex-1',
                                Number(savedResponse?.value) === value
                                    ? 'border-gray-400 border-[1px]'
                                    : 'border-white border-[1px]',
                                bgColor,
                                textColor,
                                index === 0 && 'rounded-l-lg',
                                index === divisions - 1 && 'rounded-r-lg'
                            )}
                        >
                            <span className="text-md font-[600]">{value}</span>
                        </div>
                    )
                })}
            </div>
            <div className="w-full max-w-[470px] flex gap-2 justify-start mt-3 px-1">
                <span
                    className="text-gray-500 font-[450] text-[13px] text-left"
                    style={{ minWidth: 35 }}
                >
                    0 - {minLabel}
                </span>
                <span
                    className="text-gray-500 font-[450] text-[13px] text-right"
                    style={{ minWidth: 35 }}
                >
                    10 - {maxLabel}
                </span>
            </div>
        </>
    )
}

export default NpsScale
